import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { fmtUSD, fmtDate, PARTNER_LABELS, PARTNER_KEYS } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Receipt, Pencil } from "lucide-react";
import { FileUpload } from "@/components/FileUpload";
import { FileLink } from "@/components/FileGallery";
import { TableSummaryRow } from "@/components/TableSummaryRow";
import { useAuth } from "@/context/AuthContext";

export default function Expenses() {
    const { canWrite } = useAuth();
    const [rows, setRows] = useState([]);
    const [parcels, setParcels] = useState([]);
    const [jewelry, setJewelry] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editId, setEditId] = useState(null); // when set, dialog is in edit mode
    const [form, setForm] = useState(initialForm());

    async function load() {
        setLoading(true);
        try {
            // Include sold items so expenses can still be attached after a sale.
            const [e, p, j, c] = await Promise.all([
                api.get("/expenses"),
                api.get("/parcels", { params: { include_sold: true } }),
                api.get("/jewelry", { params: { include_sold: true } }),
                api.get("/expenses/categories"),
            ]);
            setRows(e.data); setParcels(p.data); setJewelry(j.data);
            setCategories(c.data?.categories || []);
        } catch { toast.error("Failed to load"); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    function openCreate() {
        setEditId(null);
        setForm(initialForm());
        setOpen(true);
    }

    function openEdit(row) {
        setEditId(row.id);
        setForm({
            target_type: row.target_type || "general",
            target_id: row.target_id || "",
            category: row.category || "",
            amount: row.amount ?? "",
            paid_by: row.paid_by || "bashari",
            date: row.date || new Date().toISOString().slice(0, 10),
            notes: row.notes || "",
            receipts: row.receipts || [],
        });
        setOpen(true);
    }

    async function submit(e) {
        e.preventDefault();
        setSaving(true);
        try {
            const isGeneral = form.target_type === "general";
            const payload = {
                target_type: isGeneral ? null : form.target_type,
                target_id: isGeneral ? null : form.target_id,
                category: form.category,
                amount: Number(form.amount),
                paid_by: form.paid_by,
                date: form.date,
                notes: form.notes || null,
                receipts: form.receipts || [],
            };
            if (editId) {
                await api.patch(`/expenses/${editId}`, payload);
                toast.success("Expense updated");
            } else {
                await api.post("/expenses", payload);
                toast.success("Expense logged");
            }
            setForm(initialForm()); setEditId(null); setOpen(false); load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
        finally { setSaving(false); }
    }

    async function remove(id) {
        if (!confirm("Delete expense?")) return;
        try { await api.delete(`/expenses/${id}`); load(); } catch { toast.error("Failed"); }
    }

    const targetOptions = form.target_type === "parcel"
        ? parcels.map(p => ({ value: p.id, label: `${p.code}${p.name ? ` — ${p.name}` : ""}` }))
        : jewelry.map(j => ({ value: j.id, label: `${j.name || j.jewelry_type} (${j.parcel_snapshot?.code})` }));

    return (
        <div className="space-y-6 animate-fade-up" data-testid="expenses-page">
            <header className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">Costs</div>
                    <h1 className="font-display text-4xl sm:text-5xl leading-none tracking-tight">Expenses</h1>
                </div>
                {canWrite && (
                <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditId(null); } }}>
                    <DialogTrigger asChild>
                        <Button onClick={openCreate} data-testid="new-expense-button" className="rounded-none bg-stone-900 text-stone-50 hover:bg-stone-800">
                            <Plus className="h-4 w-4 mr-2" /> Log expense
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-[#faf7ef] border-stone-200 rounded-none max-w-lg" data-testid="expense-dialog">
                        <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Edit expense" : "Log expense"}</DialogTitle></DialogHeader>
                        <form onSubmit={submit} className="space-y-3">
                            <Field label="Attached to">
                                <Select value={form.target_type} onValueChange={v => setForm({ ...form, target_type: v, target_id: "" })}>
                                    <SelectTrigger className="rounded-none bg-white border-stone-200" data-testid="expense-target-type"><SelectValue /></SelectTrigger>
                                    <SelectContent className="bg-[#faf7ef] border-stone-200 rounded-none">
                                        <SelectItem value="general">General (partnership overhead)</SelectItem>
                                        <SelectItem value="parcel">Parcel</SelectItem>
                                        <SelectItem value="jewelry">Jewelry</SelectItem>
                                    </SelectContent>
                                </Select>
                            </Field>
                            {form.target_type !== "general" && (
                                <Field label={form.target_type === "parcel" ? "Which parcel" : "Which jewelry"}>
                                    <Select value={form.target_id} onValueChange={v => setForm({ ...form, target_id: v })}>
                                        <SelectTrigger className="rounded-none bg-white border-stone-200" data-testid="expense-target-id"><SelectValue placeholder="Select" /></SelectTrigger>
                                        <SelectContent className="bg-[#faf7ef] border-stone-200 rounded-none max-h-64">
                                            {targetOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </Field>
                            )}
                            <Field label="Category">
                                <Input required list="expense-category-options" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="expense-list-category-input" placeholder="e.g. Shipping, Gold, Setting" />
                                <datalist id="expense-category-options">
                                    {categories.map(c => <option key={c} value={c} />)}
                                </datalist>
                            </Field>
                            <div className="grid grid-cols-2 gap-3">
                                <Field label="Amount"><Input required type="number" step="0.01" min="0" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="expense-list-amount-input" /></Field>
                                <Field label="Date"><Input required type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className="rounded-none bg-white border-stone-200" data-testid="expense-list-date-input" /></Field>
                            </div>
                            <Field label="Paid by">
                                <Select value={form.paid_by} onValueChange={v => setForm({ ...form, paid_by: v })}>
                                    <SelectTrigger className="rounded-none bg-white border-stone-200" data-testid="expense-list-paidby-select"><SelectValue /></SelectTrigger>
                                    <SelectContent className="bg-[#faf7ef] border-stone-200 rounded-none">
                                        {PARTNER_KEYS.map(pk => <SelectItem key={pk} value={pk}>{PARTNER_LABELS[pk]}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </Field>
                            <Field label="Notes"><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="rounded-none bg-white border-stone-200" /></Field>
                            <Field label="Invoice / receipt (optional)">
                                <FileUpload
                                    value={form.receipts}
                                    onChange={receipts => setForm({ ...form, receipts })}
                                    accept="image/*,application/pdf"
                                    label="Upload invoice or receipt"
                                    testid="expense-receipts"
                                />
                            </Field>
                            <DialogFooter>
                                <Button type="submit" disabled={saving || (form.target_type !== "general" && !form.target_id)} className="rounded-none bg-emerald-600 hover:bg-emerald-500 text-white" data-testid="save-expense-list-button">
                                    {saving ? "Saving…" : (editId ? "Save changes" : "Save")}
                                </Button>
                            </DialogFooter>
                        </form>
                    </DialogContent>
                </Dialog>
                )}
            </header>

            {loading ? (
                <div className="text-stone-500 text-sm">Loading…</div>
            ) : rows.length === 0 ? (
                <div className="border border-stone-200 p-10 text-center bg-[#f5efe0]">
                    <Receipt className="mx-auto h-8 w-8 text-stone-500" strokeWidth={1.2} />
                    <h3 className="font-display text-xl mt-4">No expenses yet</h3>
                    <p className="text-sm text-stone-500 mt-1">Attach costs to parcels or jewelry — or log general partnership overhead.</p>
                </div>
            ) : (
                <div className="border border-stone-200 overflow-x-auto" data-testid="expenses-table">
                    <table className="w-full text-sm min-w-[800px]">
                        <thead>
                            <tr className="border-b border-stone-200 text-[10px] uppercase tracking-widest text-stone-500 text-left">
                                <th className="py-3 px-4">Date</th>
                                <th className="py-3 px-4">Type</th>
                                <th className="py-3 px-4">Target</th>
                                <th className="py-3 px-4">Category</th>
                                <th className="py-3 px-4">Paid by</th>
                                <th className="py-3 px-4 text-right">Amount</th>
                                <th className="py-3 px-4">Invoice</th>
                                <th className="py-3 px-4"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(e => (
                                <tr key={e.id} className="border-b border-stone-200/70 hover:bg-stone-100" data-testid={`expense-row-${e.id}`}>
                                    <td className="py-3 px-4 text-stone-500">{fmtDate(e.date)}</td>
                                    <td className="py-3 px-4 text-stone-500 text-[10px] uppercase tracking-widest">{e.target_type || "general"}</td>
                                    <td className="py-3 px-4">
                                        {e.target_type ? (
                                            <Link to={e.target_type === "parcel" ? `/parcels/${e.target_id}` : `/jewelry/${e.target_id}`} className="text-stone-800 hover:text-emerald-700 font-mono text-xs">
                                                {e.target_label}
                                            </Link>
                                        ) : (
                                            <span className="text-stone-500 italic text-xs">— general —</span>
                                        )}
                                    </td>
                                    <td className="py-3 px-4 text-stone-700">{e.category}</td>
                                    <td className="py-3 px-4 text-stone-700">{PARTNER_LABELS[e.paid_by]}</td>
                                    <td className="py-3 px-4 text-right num text-rose-600">{fmtUSD(e.amount)}</td>
                                    <td className="py-3 px-4">
                                        {(e.receipts || []).map((f, idx) => (
                                            <FileLink key={f.id || idx} file={f} label={f.original_filename} testid={`expense-receipt-${e.id}-${idx}`} />
                                        ))}
                                    </td>
                                    <td className="py-3 px-4 text-right">
                                        {canWrite && (
                                            <div className="flex items-center justify-end gap-3">
                                                <button onClick={() => openEdit(e)} className="text-stone-400 hover:text-emerald-700" data-testid={`edit-expense-row-${e.id}`} title="Edit expense">
                                                    <Pencil className="h-4 w-4" strokeWidth={1.5} />
                                                </button>
                                                <button onClick={() => remove(e.id)} className="text-stone-400 hover:text-rose-600" data-testid={`delete-expense-row-${e.id}`} title="Delete expense">
                                                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <TableSummaryRow
                            rows={rows}
                            testid="expenses-summary-row"
                            cells={[
                                { type: "label", text: `${rows.length} rows` },
                                null, null, null, null,
                                { type: "sum-usd", get: r => r.amount, testid: "expenses-summary-amount", accent: "rose-600" },
                                null, null,
                            ]}
                        />
                    </table>
                </div>
            )}
        </div>
    );
}

const initialForm = () => ({
    target_type: "parcel", target_id: "", category: "", amount: "",
    paid_by: "bashari", date: new Date().toISOString().slice(0, 10), notes: "",
    receipts: [],
});

const Field = ({ label, children }) => (
    <div><Label className="text-[10px] uppercase tracking-widest text-stone-500 mb-1.5 block">{label}</Label>{children}</div>
);
